import { createContext, useCallback, useEffect, useState } from 'react';
import useContextHook from '../useContextHook';
import PropTypes from 'prop-types';
import { Outlet } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';

const Context = createContext(null);
Context.displayName = 'AppSync';

const client = generateClient();

export const Provider = ({ children = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const query = useCallback(async (query, variables, options) => {
    try {
      setLoading(true);
      setError(null);
      const response = await client.graphql({
        query,
        variables,
        ...options
      });
      return response.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const mutate = useCallback(async (mutation, variables, options) => {
    try {
      setLoading(true);
      setError(null);
      const response = await client.graphql({
        query: mutation,
        variables,
        ...options
      });
      return response.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const subscribe = useCallback(
    (subscription, variables = {}, options = {}) => {
      return client.graphql({
        query: subscription,
        variables,
        ...options
      });
    },
    []
  );

  const value = {
    query,
    mutate,
    subscribe,
    loading,
    error
  };

  return (
    <Context.Provider value={value}>{children || <Outlet />}</Context.Provider>
  );
};

Provider.propTypes = { children: PropTypes.node };

export const useAppSync = () => useContextHook(Context);

// Helper hooks for common GraphQL operations
export const useQuery = (query, variables, options) => {
  const { query: executeQuery, loading, error } = useAppSync();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await executeQuery(query, variables, options);
      setData(result);
    } catch (err) {
      // Error is handled by the context
    }
  }, [query, executeQuery, variables, options]);

  useEffect(() => {
    let isMounted = true;

    const execute = async () => {
      if (isMounted) {
        await fetchData();
      }
    };

    execute();

    return () => {
      isMounted = false;
    };
  }, [fetchData]);

  return { data, loading, error };
};

export const useMutation = (mutation) => {
  const { mutate, loading, error } = useAppSync();

  const execute = async (variables, options) => {
    return await mutate(mutation, variables, options);
  };

  return [execute, { loading, error }];
};

export const useSubscription = (subscription, variables, options) => {
  const { subscribe } = useAppSync();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sub = subscribe(subscription, variables, options).subscribe({
      next: ({ data }) => {
        setData(data);
      },
      error: (err) => {
        setError(err);
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, [variables, options, subscribe, subscription]);

  return { data, error };
};
